import { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { colors, s16 } from '@ardoq/design-tokens';
import {
  CheckCircleIcon,
  ErrorIcon,
  ICON_SELECTOR,
  IconSize,
  InfoIcon,
  ProductUpdatesIcon,
  SaveIcon,
  VisibilityIcon,
  WarningIcon,
} from '@ardoq/icons';
import { LINK_COLOR_CSS_VARIABLE } from '@ardoq/link';
import { text2 } from '@ardoq/typography';
import { ArdoqError } from '@ardoq/common-helpers';
import { PersonalSetting } from '@ardoq/api-types';

type NotificationContainerProps = {
  // NB: Passing CSS values as props is a bad idea and we shouldn't do it,
  // but because this is private we do it anyway to keep things simple. :/
  backgroundColor: string;
  borderColor: string;
  iconColor: string;
  textColor: string;
  maxWidth?: CSSProperties['maxWidth'];
  alignItems?: CSSProperties['alignItems'];
};

type NotificationContainerStyleProps = {
  // NB: Passing CSS values as props is a bad idea and we shouldn't do it,
  // but because this is private we do it anyway to keep things simple. :/
  $backgroundColor: string;
  $borderColor: string;
  $iconColor: string;
  $textColor: string;
  $maxWidth?: CSSProperties['maxWidth'];
  $alignItems?: CSSProperties['alignItems'];
};

// Internal components for all Notification types

export type NotificationProps = HTMLAttributes<HTMLDivElement> &
  Pick<NotificationContainerProps, 'maxWidth' | 'alignItems'> & {
    icon?: ReactNode | null;
  };

export type NewFeatureNotificationData = {
  notificationKey: PersonalSetting;
  notificationMessage: string | ReactNode;
};

export const NotificationWrapper = styled.div<{ $maxWidth?: number }>`
  max-width: ${props => (props.$maxWidth ? `${props.$maxWidth}px` : 'auto')};
  width: 100%;
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: ${s16};
`;

const NotificationContainer = styled.div<NotificationContainerStyleProps>`
  ${text2};
  display: flex;
  gap: ${s16};
  border-radius: 4px;
  padding: ${s16};

  ${props => css`
    background: ${props.$backgroundColor};
    border: 1px solid ${props.$borderColor};
    color: ${props.$textColor};
    ${LINK_COLOR_CSS_VARIABLE}: ${props.$textColor};
    align-items: ${props.$alignItems};

    > ${ICON_SELECTOR} {
      color: ${props.$iconColor};
    }
  `}
  ${({ $maxWidth }) =>
    $maxWidth &&
    css`
      max-width: ${$maxWidth};
    `}
`;

export const NOTIFICATION_SELECTOR = NotificationContainer.toString();

const ContentContainer = styled.div`
  flex: 1;
  overflow-wrap: anywhere;
  margin: auto 0;
`;

type BaseNotificationProps = NotificationContainerProps & {
  icon: ReactNode;
  children: ReactNode;
};

const BaseNotification = ({
  icon,
  children,
  backgroundColor,
  borderColor,
  iconColor,
  textColor,
  maxWidth,
  alignItems,
  ...rest
}: BaseNotificationProps) => {
  return (
    <NotificationContainer
      $backgroundColor={backgroundColor}
      $borderColor={borderColor}
      $iconColor={iconColor}
      $textColor={textColor}
      $maxWidth={maxWidth}
      $alignItems={alignItems}
      role="status"
      {...rest}
    >
      {icon}
      <ContentContainer>{children}</ContentContainer>
    </NotificationContainer>
  );
};

// The actual component interfaces

export const DefaultNotification = ({
  children,
  icon,
  ...rest
}: NotificationProps) => {
  return (
    <BaseNotification
      backgroundColor={colors.surfaceStatusNeutral}
      borderColor={colors.borderAction}
      iconColor={colors.icon}
      textColor={colors.textDefault}
      icon={icon === undefined ? <InfoIcon iconSize={IconSize.MEDIUM} /> : icon}
      {...rest}
    >
      {children}
    </BaseNotification>
  );
};

export const InfoNotification = ({
  children,
  icon,
  ...rest
}: NotificationProps) => {
  return (
    <BaseNotification
      backgroundColor={colors.surfaceInfo}
      borderColor={colors.borderInfo}
      iconColor={colors.iconInfo}
      textColor={colors.textInfo}
      icon={icon === undefined ? <InfoIcon iconSize={IconSize.MEDIUM} /> : icon}
      {...rest}
    >
      {children}
    </BaseNotification>
  );
};

export const WarningNotification = ({
  children,
  icon,
  ...rest
}: NotificationProps) => {
  return (
    <BaseNotification
      backgroundColor={colors.surfaceWarning}
      borderColor={colors.borderWarning}
      iconColor={colors.iconWarning}
      textColor={colors.textWarning}
      icon={
        icon === undefined ? <WarningIcon iconSize={IconSize.MEDIUM} /> : icon
      }
      {...rest}
    >
      {children}
    </BaseNotification>
  );
};

export const ErrorNotification = ({
  children,
  icon,
  ...rest
}: NotificationProps) => {
  return (
    <BaseNotification
      role="alert"
      backgroundColor={colors.surfaceError}
      borderColor={colors.borderError}
      iconColor={colors.iconError}
      textColor={colors.textError}
      icon={
        icon === undefined ? <ErrorIcon iconSize={IconSize.MEDIUM} /> : icon
      }
      {...rest}
    >
      {children}
    </BaseNotification>
  );
};

export const SuccessNotification = ({
  children,
  icon,
  ...rest
}: NotificationProps) => {
  return (
    <BaseNotification
      backgroundColor={colors.surfaceSuccess}
      borderColor={colors.borderSuccess}
      iconColor={colors.iconSuccess}
      textColor={colors.textSuccess}
      icon={
        icon === undefined ? (
          <CheckCircleIcon iconSize={IconSize.MEDIUM} />
        ) : (
          icon
        )
      }
      {...rest}
    >
      {children}
    </BaseNotification>
  );
};

export const SaveNotification = ({
  children,
  icon,
  ...rest
}: NotificationProps) => {
  return (
    <BaseNotification
      backgroundColor={colors.surfaceStatusNeutral}
      borderColor={colors.borderAction}
      iconColor={colors.icon}
      textColor={colors.textDefault}
      icon={icon === undefined ? <SaveIcon iconSize={IconSize.MEDIUM} /> : icon}
      {...rest}
    >
      {children}
    </BaseNotification>
  );
};

export const PreviewNotification = ({
  children,
  icon,
  ...rest
}: NotificationProps) => {
  return (
    <BaseNotification
      backgroundColor={colors.surfaceBrand}
      borderColor={colors.borderBrand}
      iconColor={colors.iconBrand}
      textColor={colors.textBrand}
      icon={
        icon === undefined ? (
          <VisibilityIcon iconSize={IconSize.MEDIUM} />
        ) : (
          icon
        )
      }
      {...rest}
    >
      {children}
    </BaseNotification>
  );
};

export const NewFeatureNotification = ({
  children,
  icon,
  ...rest
}: NotificationProps) => {
  return (
    <BaseNotification
      backgroundColor={colors.surfaceBrand}
      borderColor={colors.borderBrand}
      iconColor={colors.iconBrand}
      textColor={colors.textBrand}
      icon={
        icon === undefined ? (
          <ProductUpdatesIcon iconSize={IconSize.MEDIUM} />
        ) : (
          icon
        )
      }
      alignItems={'center'}
      {...rest}
    >
      {children}
    </BaseNotification>
  );
};

type ParseAPIErrorNotificationProps = {
  error: ArdoqError;
  maxWidth?: React.CSSProperties['maxWidth'];
};

export const ParseAPIErrorNotification = ({
  error,
  maxWidth,
}: ParseAPIErrorNotificationProps) => (
  <ErrorNotification maxWidth={maxWidth}>
    {error.userMessage}
    {error.traceId ? (
      <>
        <br />
        {`Trace ID: ${error.traceId}`}
      </>
    ) : (
      ''
    )}
  </ErrorNotification>
);
